import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Command as CommandIcon, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../../ui/dialog';
import i18n from '../../../../i18n/config';

export const VISUAL_PICKER_INTERACTION_MODES = {
  INSTANT: 'instant',
  SELECT_THEN_PLACE: 'select_then_place',
};

export const isSelectThenPlaceVisualPickerMode = (interactionMode) => (
  interactionMode === VISUAL_PICKER_INTERACTION_MODES.SELECT_THEN_PLACE
);

export const getVisualPickerDialogVisibilityClass = (isTemporarilyHidden) => (
  isTemporarilyHidden ? 'report-draft-editor__visual-picker-dialog--drag-hidden' : ''
);

export const resolveVisualPickerInsertAtCursorItem = ({
  flattenedItems,
  selectedItemId,
  selectedIndex,
}) => {
  const candidates = Array.isArray(flattenedItems) ? flattenedItems : [];
  if (!candidates.length) {
    return null;
  }

  if (selectedItemId) {
    const matched = candidates.find((entry) => entry?.item?.id === selectedItemId)?.item;
    if (matched && !matched.disabled) {
      return matched;
    }
  }

  const activeItem = candidates[selectedIndex]?.item;
  if (activeItem && !activeItem.disabled) {
    return activeItem;
  }

  return candidates.find((entry) => entry?.item && !entry.item.disabled)?.item || null;
};

const applyVisualPickerChartDragImage = (event, item) => {
  const dataTransfer = event?.dataTransfer;
  const dragPayload = item?.dragPayload;
  if (
    typeof document === 'undefined'
    || !dataTransfer
    || typeof dataTransfer.setDragImage !== 'function'
    || dragPayload?.visualType !== 'chart'
  ) {
    return;
  }

  const ghost = document.createElement('div');
  ghost.setAttribute('aria-hidden', 'true');
  ghost.textContent = item?.label || i18n.t('reports.review.editor.toolbar.chart');
  Object.assign(ghost.style, {
    position: 'fixed',
    top: '-9999px',
    left: '-9999px',
    pointerEvents: 'none',
    zIndex: '-1',
    padding: '8px 10px',
    borderRadius: '10px',
    border: '1px solid rgba(21, 154, 139, 0.35)',
    background: 'rgba(255, 255, 255, 0.96)',
    color: '#164e63',
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '1',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
    whiteSpace: 'nowrap',
  });

  document.body.appendChild(ghost);
  try {
    dataTransfer.setDragImage(ghost, 14, 14);
  } catch (error) {
    // ignore drag image failures; native drag can continue
  }

  if (typeof window === 'undefined') {
    ghost.remove();
    return;
  }
  window.setTimeout(() => {
    ghost.remove();
  }, 0);
};

const findNextEnabledIndex = (items, startIndex, delta) => {
  if (!Array.isArray(items) || items.length === 0) {
    return 0;
  }
  const hasEnabled = items.some(entry => !entry?.item?.disabled);
  if (!hasEnabled) {
    return Math.max(0, Math.min(startIndex, items.length - 1));
  }

  let nextIndex = startIndex;
  for (let attempt = 0; attempt < items.length; attempt += 1) {
    nextIndex = (nextIndex + delta + items.length) % items.length;
    if (!items[nextIndex]?.item?.disabled) {
      return nextIndex;
    }
  }
  return 0;
};

const DEFAULT_SERIES_COLORS = ['#3A6A7A', '#3A5A2A', '#8B4513', '#4682B4'];

const buildSparklinePath = (values, width, height, padding = 8) => {
  if (!Array.isArray(values) || values.length === 0) {
    return '';
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const innerWidth = Math.max(1, width - (padding * 2));
  const innerHeight = Math.max(1, height - (padding * 2));
  const span = max - min || 1;
  return values.map((value, index) => {
    const x = padding + ((values.length === 1 ? 0 : index / (values.length - 1)) * innerWidth);
    const y = padding + (innerHeight - (((value - min) / span) * innerHeight));
    return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
};

const buildPreviewSeries = (chart) => {
  const data = Array.isArray(chart?.data) ? chart.data : [];
  if (!data.length) {
    return [];
  }
  const stride = Math.max(1, Math.ceil(data.length / 28));
  const sampled = data.filter((_, index) => index % stride === 0).slice(-28);
  const configuredSeries = Array.isArray(chart?.series) && chart.series.length
    ? chart.series
    : [{ key: chart?.yKey || 'value', label: chart?.yKey || 'value', color: '#455642' }];
  return configuredSeries.slice(0, 3).map((series, index) => ({
    key: series.key,
    label: series.label,
    color: series.color || DEFAULT_SERIES_COLORS[index % DEFAULT_SERIES_COLORS.length],
    values: sampled.map(row => Number(row?.[series.key]) || 0),
  })).filter(series => series.values.some(value => Number.isFinite(value)));
};

function MiniChartPreview({ preview }) {
  const { t } = useTranslation();
  const isBlank = Boolean(preview?.blank);
  const series = buildPreviewSeries(preview?.chart);
  const width = 240;
  const height = 96;

  if (isBlank) {
    const blankValuesA = [12, 18, 15, 24, 22, 28, 26];
    const blankValuesB = [9, 12, 11, 16, 15, 19, 18];
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="report-draft-editor__visual-picker-thumb-svg" aria-hidden="true">
        <rect x="0" y="0" width={width} height={height} fill="#f8faf9" />
        {[16, 32, 48, 64, 80].map((y) => (
          <line key={`grid-y-${y}`} x1="8" y1={y} x2={width - 8} y2={y} stroke="#e7ece9" strokeWidth="1" />
        ))}
        {[24, 64, 104, 144, 184, 224].map((x) => (
          <line key={`grid-x-${x}`} x1={x} y1="8" x2={x} y2={height - 10} stroke="#eef2ef" strokeWidth="1" />
        ))}
        <path d={buildSparklinePath(blankValuesA, width, height)} fill="none" stroke="#3A6A7A" strokeWidth="2" strokeLinecap="round" />
        <path d={buildSparklinePath(blankValuesB, width, height)} fill="none" stroke="#3A5A2A" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
      </svg>
    );
  }

  if (!series.length) {
    return (
      <div className="report-draft-editor__visual-picker-thumb-empty">
        <span>{t('common.notAvailable')}</span>
      </div>
    );
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="report-draft-editor__visual-picker-thumb-svg" aria-hidden="true">
      <rect x="0" y="0" width={width} height={height} fill="#f8faf9" />
      {[16, 32, 48, 64, 80].map((y) => (
        <line key={`grid-y-${y}`} x1="8" y1={y} x2={width - 8} y2={y} stroke="#e7ece9" strokeWidth="1" />
      ))}
      {[24, 64, 104, 144, 184, 224].map((x) => (
        <line key={`grid-x-${x}`} x1={x} y1="8" x2={x} y2={height - 10} stroke="#eef2ef" strokeWidth="1" />
      ))}
      {series.map((line) => (
        <path
          key={line.key}
          d={buildSparklinePath(line.values, width, height)}
          fill="none"
          stroke={line.color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

MiniChartPreview.propTypes = {
  preview: PropTypes.shape({
    blank: PropTypes.bool,
    chart: PropTypes.object,
  }),
};

MiniChartPreview.defaultProps = {
  preview: null,
};

function MiniMapPreview({ preview }) {
  const zones = Array.isArray(preview?.fallbackCoverage?.zones) ? preview.fallbackCoverage.zones : [];
  const stations = Array.isArray(preview?.fallbackCoverage?.stations) ? preview.fallbackCoverage.stations : [];
  const isBlank = Boolean(preview?.blank);

  return (
    <div className="report-draft-editor__visual-picker-map-thumb" aria-hidden="true">
      <svg viewBox="0 0 240 96" className="report-draft-editor__visual-picker-thumb-svg">
        <defs>
          <linearGradient id="mapThumbBg" x1="0%" x2="100%" y1="0%" y2="100%">
            <stop offset="0%" stopColor="#eef4ef" />
            <stop offset="100%" stopColor="#f7faf8" />
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="240" height="96" fill="url(#mapThumbBg)" />
        <path d="M18 21 L90 12 L128 32 L76 47 Z" fill="#dce7dc" stroke="#c7d7c7" />
        <path d="M82 46 L141 31 L212 43 L164 78 L95 70 Z" fill="#cfe0cf" stroke="#b8cdb8" />
        <path d="M25 55 L79 48 L95 71 L38 83 Z" fill="#e6efe6" stroke="#d2e0d2" />
        {!isBlank && stations.slice(0, 8).map((_, index) => {
          const x = 32 + ((index * 23) % 168);
          const y = 26 + ((index * 17) % 48);
          return (
            <g key={`station-${index}`}>
              <circle cx={x} cy={y} r="3.2" fill="#2f5746" opacity="0.92" />
              <circle cx={x} cy={y} r="6.2" fill="#2f5746" opacity="0.14" />
            </g>
          );
        })}
      </svg>
      <div className="report-draft-editor__visual-picker-map-thumb-overlay">
        <span>{isBlank ? 'Blank map' : `${zones.length || 0} zones`}</span>
        <span>{isBlank ? 'Editable' : `${stations.length || 0} stations`}</span>
      </div>
    </div>
  );
}

MiniMapPreview.propTypes = {
  preview: PropTypes.shape({
    blank: PropTypes.bool,
    fallbackCoverage: PropTypes.object,
  }),
};

MiniMapPreview.defaultProps = {
  preview: null,
};

export function VisualPreviewThumb({ item }) {
  if (item?.preview?.visualType === 'map') {
    return <MiniMapPreview preview={item.preview} />;
  }
  return <MiniChartPreview preview={item?.preview || null} />;
}

VisualPreviewThumb.propTypes = {
  item: PropTypes.shape({
    preview: PropTypes.shape({
      visualType: PropTypes.string,
    }),
  }),
};

VisualPreviewThumb.defaultProps = {
  item: null,
};

export function ReportDraftVisualPickerDialog({
  open,
  onOpenChange,
  title,
  description,
  sections,
  searchPlaceholder,
  emptyLabel,
  interactionMode,
  selectedItemId,
  onSelectedItemChange,
  onInsertAtCursorItem,
  insertAtCursorLabel,
  placementHint,
  showPlacementHints,
  onDragStartItem,
  onDragEndItem,
  isTemporarilyHidden,
}) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [internalSelectedItemId, setInternalSelectedItemId] = useState(null);
  const inputRef = useRef(null);
  const isSelectThenPlaceMode = isSelectThenPlaceVisualPickerMode(interactionMode);
  const isSelectionControlled = typeof selectedItemId === 'string';
  const selectedItemIdValue = isSelectionControlled ? selectedItemId : internalSelectedItemId;

  const filteredSections = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return (sections || [])
      .filter(section => Array.isArray(section.items) && section.items.length > 0)
      .map((section) => {
        if (!normalizedQuery) {
          return section;
        }
        const items = section.items.filter((item) => {
          const haystack = [section.label, item.label, item.description, item.shortcut]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(normalizedQuery);
        });
        return {
          ...section,
          items,
        };
      })
      .filter(section => section.items.length > 0);
  }, [query, sections]);

  const flattenedItems = useMemo(
    () => filteredSections.flatMap((section) => section.items.map((item) => ({ section, item }))),
    [filteredSections]
  );

  const commitSelectedItem = React.useCallback((item) => {
    const nextItem = item && !item.disabled ? item : null;
    const nextId = nextItem?.id || null;
    if (!isSelectionControlled) {
      setInternalSelectedItemId((current) => (current === nextId ? current : nextId));
    }
    if (typeof onSelectedItemChange === 'function') {
      onSelectedItemChange(nextItem);
    }
  }, [isSelectionControlled, onSelectedItemChange]);

  const insertAtCursorItem = useMemo(
    () => resolveVisualPickerInsertAtCursorItem({
      flattenedItems,
      selectedItemId: selectedItemIdValue,
      selectedIndex,
    }),
    [flattenedItems, selectedIndex, selectedItemIdValue]
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedIndex(0);
      if (!isSelectionControlled) {
        setInternalSelectedItemId(null);
      }
      return;
    }
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isSelectionControlled, open]);

  useEffect(() => {
    if (!flattenedItems.length) {
      setSelectedIndex(0);
      return;
    }
    setSelectedIndex((current) => {
      const bounded = Math.min(Math.max(current, 0), flattenedItems.length - 1);
      if (!flattenedItems[bounded]?.item?.disabled) {
        return bounded;
      }
      return findNextEnabledIndex(flattenedItems, bounded, 1);
    });
  }, [flattenedItems]);

  useEffect(() => {
    if (!open || !isSelectThenPlaceMode) {
      return;
    }
    const nextDefault = resolveVisualPickerInsertAtCursorItem({
      flattenedItems,
      selectedItemId: selectedItemIdValue,
      selectedIndex,
    });
    if (nextDefault && nextDefault.id !== selectedItemIdValue) {
      commitSelectedItem(nextDefault);
    }
  }, [
    commitSelectedItem,
    flattenedItems,
    isSelectThenPlaceMode,
    open,
    selectedIndex,
    selectedItemIdValue,
  ]);

  const handleSelect = (item) => {
    if (!item || item.disabled || typeof item.onSelect !== 'function') {
      return;
    }
    item.onSelect();
    onOpenChange(false);
  };

  const handleInsertAtCursor = (item = insertAtCursorItem) => {
    if (!isSelectThenPlaceMode) {
      return;
    }
    if (!item || item.disabled || typeof onInsertAtCursorItem !== 'function') {
      return;
    }
    onInsertAtCursorItem(item);
    onOpenChange(false);
  };

  const handleKeyDown = (event) => {
    if (!open) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!flattenedItems.length) {
        return;
      }
      setSelectedIndex((current) => {
        const nextIndex = findNextEnabledIndex(flattenedItems, current, 1);
        if (isSelectThenPlaceMode) {
          commitSelectedItem(flattenedItems[nextIndex]?.item || null);
        }
        return nextIndex;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!flattenedItems.length) {
        return;
      }
      setSelectedIndex((current) => {
        const nextIndex = findNextEnabledIndex(flattenedItems, current, -1);
        if (isSelectThenPlaceMode) {
          commitSelectedItem(flattenedItems[nextIndex]?.item || null);
        }
        return nextIndex;
      });
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (isSelectThenPlaceMode) {
        handleInsertAtCursor();
      } else {
        handleSelect(flattenedItems[selectedIndex]?.item);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onOpenChange(false);
    }
  };

  let itemCursor = 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={!isSelectThenPlaceMode}>
      <DialogContent
        className={[
          'report-draft-editor__command-palette-dialog',
          'report-draft-editor__visual-picker-dialog',
          getVisualPickerDialogVisibilityClass(isTemporarilyHidden),
        ].filter(Boolean).join(' ')}
        showCloseButton
        showOverlay={!isSelectThenPlaceMode}
        aria-hidden={isTemporarilyHidden || undefined}
        onKeyDown={handleKeyDown}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{description}</DialogDescription>

        <div className="report-draft-editor__command-palette-head">
          <div className="report-draft-editor__command-palette-title-wrap">
            <CommandIcon size={16} />
            <span className="report-draft-editor__command-palette-title">{title}</span>
          </div>
        </div>

        <div className="report-draft-editor__command-palette-search">
          <Search size={15} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelectedIndex(0);
            }}
            placeholder={searchPlaceholder}
            className="report-draft-editor__command-palette-input"
          />
        </div>

        <div className="report-draft-editor__command-palette-list" role="listbox" aria-label={title}>
          {filteredSections.length === 0 ? (
            <div className="report-draft-editor__command-palette-empty">{emptyLabel}</div>
          ) : (
            filteredSections.map((section) => {
              const sectionStartIndex = itemCursor;
              itemCursor += section.items.length;
              return (
                <div key={section.id} className="report-draft-editor__command-palette-section">
                  {section.label ? (
                    <div className="report-draft-editor__command-palette-section-label">{section.label}</div>
                  ) : null}
                  <div className="report-draft-editor__visual-picker-grid">
                    {section.items.map((item, itemIndex) => {
                      const flatIndex = sectionStartIndex + itemIndex;
                      const isActive = flatIndex === selectedIndex;
                      const isPicked = isSelectThenPlaceMode && selectedItemIdValue === item.id;
                      const Icon = item.icon;
                      const isDisabled = Boolean(item.disabled);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          role="option"
                          aria-selected={Boolean(isPicked || isActive)}
                          aria-disabled={isDisabled}
                          disabled={isDisabled}
                          draggable={!isDisabled}
                          className={[
                            'report-draft-editor__visual-picker-card',
                            isActive ? 'is-active' : '',
                            isPicked ? 'is-selected' : '',
                            isDisabled ? 'is-disabled' : '',
                          ].filter(Boolean).join(' ')}
                          onMouseEnter={() => {
                            if (!isDisabled) {
                              setSelectedIndex(flatIndex);
                            }
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            if (isDisabled) {
                              return;
                            }
                            setSelectedIndex(flatIndex);
                            if (isSelectThenPlaceMode) {
                              commitSelectedItem(item);
                              return;
                            }
                            handleSelect(item);
                          }}
                          onDragStart={(event) => {
                            if (isDisabled || !item?.dragPayload) {
                              event.preventDefault();
                              return;
                            }
                            try {
                              if (isSelectThenPlaceMode) {
                                commitSelectedItem(item);
                              }
                              const payload = JSON.stringify(item.dragPayload);
                              event.dataTransfer.effectAllowed = 'copy';
                              event.dataTransfer.setData('application/x-report-draft-visual-insert', payload);
                              event.dataTransfer.setData('text/plain', payload);
                              applyVisualPickerChartDragImage(event, item);
                              if (typeof onDragStartItem === 'function') {
                                onDragStartItem(item, event);
                              }
                            } catch (error) {
                              event.preventDefault();
                            }
                          }}
                          onDragEnd={(event) => {
                            if (typeof onDragEndItem === 'function') {
                              onDragEndItem(item, event);
                            }
                          }}
                        >
                          <div className="report-draft-editor__visual-picker-card-thumb">
                            <VisualPreviewThumb item={item} />
                          </div>
                          <div className="report-draft-editor__visual-picker-card-meta">
                            <div className="report-draft-editor__visual-picker-card-title-row">
                              <span className="report-draft-editor__visual-picker-card-title">
                                {item.label}
                              </span>
                              {Icon ? (
                                <span className="report-draft-editor__visual-picker-card-icon" aria-hidden="true">
                                  <Icon size={14} />
                                </span>
                              ) : null}
                            </div>
                            {item.description ? (
                              <span className="report-draft-editor__visual-picker-item-description">
                                {item.description}
                              </span>
                            ) : null}
                            {isDisabled && item.disabledReason ? (
                              <span className="report-draft-editor__visual-picker-item-disabled-reason">
                                {item.disabledReason}
                              </span>
                            ) : null}
                            {item.shortcut ? (
                              <span className="report-draft-editor__visual-picker-card-shortcut">
                                {item.shortcut}
                              </span>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {isSelectThenPlaceMode ? (
          <div className="report-draft-editor__visual-picker-footer">
            <div className="report-draft-editor__visual-picker-footer-copy">
              {showPlacementHints && placementHint ? (
                <p className="report-draft-editor__visual-picker-placement-hint">{placementHint}</p>
              ) : null}
            </div>
            <div className="report-draft-editor__visual-picker-footer-actions">
              <button
                type="button"
                className="report-draft-editor__visual-picker-insert-button"
                onClick={() => handleInsertAtCursor()}
                disabled={!insertAtCursorItem}
              >
                {insertAtCursorLabel}
              </button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

ReportDraftVisualPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  emptyLabel: PropTypes.string.isRequired,
  interactionMode: PropTypes.oneOf(Object.values(VISUAL_PICKER_INTERACTION_MODES)),
  selectedItemId: PropTypes.string,
  onSelectedItemChange: PropTypes.func,
  onInsertAtCursorItem: PropTypes.func,
  insertAtCursorLabel: PropTypes.string,
  placementHint: PropTypes.string,
  showPlacementHints: PropTypes.bool,
  onDragStartItem: PropTypes.func,
  onDragEndItem: PropTypes.func,
  isTemporarilyHidden: PropTypes.bool,
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      shortcut: PropTypes.string,
      icon: PropTypes.elementType,
      preview: PropTypes.shape({
        visualType: PropTypes.string,
        blank: PropTypes.bool,
        chart: PropTypes.object,
        fallbackCoverage: PropTypes.object,
      }),
      disabled: PropTypes.bool,
      disabledReason: PropTypes.string,
      dragPayload: PropTypes.shape({
        kind: PropTypes.string,
        visualType: PropTypes.string,
        sourceKind: PropTypes.string,
        itemId: PropTypes.string,
        nodeData: PropTypes.object,
      }),
      onSelect: PropTypes.func,
    })).isRequired,
  })).isRequired,
};

ReportDraftVisualPickerDialog.defaultProps = {
  interactionMode: VISUAL_PICKER_INTERACTION_MODES.INSTANT,
  selectedItemId: undefined,
  onSelectedItemChange: undefined,
  onInsertAtCursorItem: undefined,
  insertAtCursorLabel: 'Insert at cursor',
  placementHint: '',
  showPlacementHints: false,
  onDragStartItem: undefined,
  onDragEndItem: undefined,
  isTemporarilyHidden: false,
};
